import React from 'react'
import { HelpDrawerWithAccordion } from '@labrav/common'
import { landingPageImages } from '../../../common/utils/productCreationUtil'
import {
  FaqQuestion,
  FlyoutProductCreationFaqSection,
  HelpDrawerFaqItems,
} from '../FlyoutProductFormHelpFaqs/ProductCreationFaqUtil'
import { CarouselWithModal } from '../../../common/components/CarouselWithModal/CarouselWithModal'

const faqItems: HelpDrawerFaqItems[] = [
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="What is a public-facing login page?"
      />
    ),
    answer: (
      <div>
        <p>
          A public-facing login page allows buyers to{' '}
          <strong>self-register and onboard</strong> onto your platform{' '}
          <strong>without requiring manual approval</strong> from your team. If
          buyers need to request access, go through a sales team, or wait for
          manual provisioning, your login page is not public-facing.
        </p>
        <ul
          style={{
            paddingLeft: '20px',
            listStyleType: 'disc',
            margin: 'revert',
          }}
        >
          <li>
            <strong>Why does this matter?</strong> AWS requires that buyers can
            start using your product immediately after subscribing unless
            otherwise stated in your listing or communication with the buyer.
          </li>
        </ul>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="Why must I provide my Application Login Page URL?"
      />
    ),
    answer: (
      <div>
        <p>
          When a buyer subscribes to your product on AWS Marketplace, their
          billing (contract) starts immediately. AWS expects that buyers can{' '}
          <strong>log in and access the product right away</strong>.
        </p>
        <ol style={{ paddingLeft: '20px' }}>
          <li>
            If buyers can <strong>self-onboard</strong>, they should be
            redirected to your <strong>Application Login Page URL</strong> after
            submitting the registration form.
          </li>
          <li>
            If onboarding <strong>requires manual approval</strong>, you must
            provide <strong>clear instructions</strong> on a notification on how
            and when buyers will receive access. Not providing this information
            can lead to AWS rejecting your listing.
          </li>
        </ol>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="What should I provide in Testing Credentials, and why?"
      />
    ),
    answer: (
      <div>
        <p>
          Testing credentials are temporary login details (username & password)
          for a <strong>demo account</strong> that allows AWS and Labra teams to
          verify your product's post-subscription flow.
        </p>
        <p>1. AWS testers need to:</p>
        <ul
          style={{
            paddingLeft: '40px',
            listStyleType: 'disc',
            margin: 'revert',
          }}
        >
          <li>Confirm that buyers can log in after subscribing.</li>
          <li>Check how your product behaves once access is granted.</li>
          <li>
            Ensure that everything aligns with AWS Marketplace guidelines.
          </li>
        </ul>
        <p>2. Note:</p>
        <ul
          style={{
            paddingLeft: '40px',
            listStyleType: 'disc',
            margin: 'revert',
          }}
        >
          <li>
            AWS will require access to your product either through a self serve
            page, test credentials or manual support by your team.
          </li>
          <li>
            Your listing <strong>won't go live</strong> until AWS completes this
            verification.
          </li>
          <li>
            You can <strong>delete the test account</strong> once your listing
            is approved.
          </li>
        </ul>
        <p>
          Only <strong>Labra and AWS teams</strong> will have access to your
          test credentials. They are used strictly for{' '}
          <strong>User Acceptance Testing (UAT)</strong> and will not be shared
          with anyone else.
        </p>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="What happens during AWS testing, and how will I know when it starts?"
      />
    ),
    answer: (
      <div>
        <p>
          1. AWS follows a structured testing process to verify your listing.
          This includes:
        </p>
        <ul
          style={{
            paddingLeft: '40px',
            listStyleType: 'disc',
            margin: 'revert',
          }}
        >
          <li>
            <strong>Checking your listing information</strong> for accuracy and
            compliance.
          </li>
          <li>
            <strong>Visiting your website</strong> to ensure consistency with
            AWS Marketplace claims.
          </li>
          <li>
            <strong>Subscribing to your product</strong> on AWS Marketplace to
            test the buyer flow.
          </li>
          <li>
            <strong>Onboarding as a buyer</strong> to confirm access and
            usability.
          </li>
          <li>
            <strong>Reviewing product functionality</strong> to ensure it aligns
            with your listing.
          </li>
        </ul>
        <p>2. How will you know?</p>
        <ul
          style={{
            paddingLeft: '40px',
            listStyleType: 'disc',
            margin: 'revert',
          }}
        >
          <li>
            Labra displays your <strong>listing status</strong> in the app
            (e.g., <strong>In Labra Testing or In AWS Testing</strong>).
          </li>
          <li>
            Once AWS subscribes to your product, you will receive a{' '}
            <strong>Labra notification</strong> with the AWS tester's details.
            You must <strong>actively assist the tester</strong> in getting
            access to your product to avoid delays.
          </li>
        </ul>
        <div
          style={{
            backgroundColor: '#E5F6FD',
            padding: '10px',
            borderRadius: '4px',
            marginTop: '10px',
          }}
        >
          <strong>Important:</strong> If you fail to respond or assist the AWS
          tester, your listing may be rejected, delaying its launch.
        </div>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="What if I don't want to provide testing credentials?"
      />
    ),
    answer: (
      <div>
        <p>
          If you don't have a public-facing login page and can't provide
          credentials, you must submit alternative materials, such as:
        </p>
        <ul
          style={{
            paddingLeft: '20px',
            listStyleType: 'disc',
            margin: 'revert',
          }}
        >
          <li>
            A <strong>detailed step-by-step guide or instructions</strong>{' '}
            explaining how and when a buyer can access the product.
          </li>
          <li>
            A <strong>demo video</strong> showcasing the user onboarding
            process.
          </li>
          <li>
            <strong>Onboarding documents</strong> outlining key steps for
            product access.
          </li>
        </ul>
        <p>
          The{' '}
          <strong>
            more information you provide, the smoother the approval process
          </strong>{' '}
          will be. Incomplete or unclear details may lead to delays or rejection
          and also a bad user experience for your buyer.
        </p>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="What does a public facing sign up page imply?"
      />
    ),
    answer: (
      <div>
        A public facing sign page means that your buyers can easily sign up and
        onboard themselves onto your platform and start using it without
        requiring any help from your team.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="How do I know if I have a public facing sign up page?"
      />
    ),
    answer: (
      <div>
        If after a buyer subscribes to your product, your customer support or
        sales team representatives are required to help them onboard onto your
        application then it means you DO NOT have a public facing sign up page.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="What is the application login page URL? "
      />
    ),
    answer: (
      <div>
        This is the link to the webpage where users can sign up and login to
        your application. Please note - Please don’t provide a link for your
        website or any other link since it could lead to a rejection of your
        listing.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="Why do I need to provide my application login page URL?"
      />
    ),
    answer: (
      <div>
        AWS requires that all buyers must have the ability to sign up onto your
        platform after they subscribe to your product on AWS Marketplace. The
        only exception for this is when your buyers cannot onboard themselves
        and require assistance from your team to gain access to your product.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="After how many seconds will my buyer be redirected to my application login page URL?"
      />
    ),
    answer: (
      <div>
        Once your buyer submits the buyer registration page form, they will
        automatically be redirected to your application login page URL in 5
        seconds.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="What testing credentials will be required to add?"
      />
    ),
    answer: (
      <div>
        You can give testing credentials from a demo account which can help AWS
        and Labra team get quick access to your application by simple signin in.
        Please note - You can delete this demo testing account once your listing
        is live on AWS Marketplace.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="What will the testing credentials be used for?"
      />
    ),
    answer: (
      <div>
        The testing credentials provided by you will only be shared with the
        Labra and AWS team for User Acceptance Testing purposes. This process
        includes testing the entire end-to-end flow of subscribing to your
        product on AWS Marketplace to finally getting access to your
        application. Till the AWS team does not get this access, they will not
        approve your listing to go live.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="What are custom instructions?"
      />
    ),
    answer: (
      <div>
        You can provide your buyer with a breif on how they can onboard
        themselves in case your product allows it or you can let them know when
        they will be onboarded onto your platform in case they can’t sign up
        themselves. This is basically to let your buyers know the next steps in
        order to gain access to your application.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={
          FlyoutProductCreationFaqSection.BUYER_REGISTRATION_REDIRECTION_SETTINGS
        }
        question="Where will custom instructions show up?"
      />
    ),
    answer: (
      <div>
        The custom instructions provided by you will be attached at the top of
        the email which Labra sends to your buyer after they submit the buyer
        registration page form. Please refer to the last image at the top of
        page to understand how it will look.
      </div>
    ),
  },
]

interface FlyoutProductRedirectionSettingsFaqsProps {
  indexModal: number
  setIndexModal: (index: number) => void
}

export const FlyoutProductRedirectionSettingsFaqs: React.FC<
  FlyoutProductRedirectionSettingsFaqsProps
> = ({ indexModal, setIndexModal }) => (
  <HelpDrawerWithAccordion
    headerTitle="Help & FAQs - Redirection Settings"
    faqItems={faqItems}
    customContent={
      <CarouselWithModal
        carouselData={landingPageImages}
        setCurrentIndex={setIndexModal}
        currentIndex={indexModal}
      />
    }
  />
)
